/**
 * Cache-safe, timezone-correct open/closed status.
 *
 * Opening hours for Weißenstadt venues are Europe/Berlin wall times. Page cache
 * freezes server-rendered labels, so every live pill recomputes from
 * data-periods in the browser using Berlin time — never the visitor's local TZ.
 */

const BERLIN_TZ = "Europe/Berlin";
const WEEKDAY_MAP = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const berlinPartsFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: BERLIN_TZ,
  weekday: "short",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/**
 * ISO date (YYYY-MM-DD) `offset` days after a Berlin calendar date.
 */
export function shiftIsoDate(isoDate, offset) {
  const [year, month, day] = String(isoDate || "")
    .split("-")
    .map((part) => Number(part));

  if (!year || !month || !day) {
    return "";
  }

  return new Date(Date.UTC(year, month - 1, day + offset))
    .toISOString()
    .slice(0, 10);
}

/**
 * @returns {{ day: number, minutes: number, hour: number, minute: number, date: string }}
 */
export function getBerlinClock(date = new Date()) {
  const parts = berlinPartsFormatter.formatToParts(date);
  const map = {};

  for (const part of parts) {
    if (part.type !== "literal") {
      map[part.type] = part.value;
    }
  }

  const day = WEEKDAY_MAP[map.weekday];
  const hour = Number(map.hour);
  const minute = Number(map.minute);
  const isoDate =
    map.year && map.month && map.day
      ? `${map.year}-${map.month}-${map.day}`
      : "";

  if (
    day === undefined ||
    Number.isNaN(hour) ||
    Number.isNaN(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    // Extremely defensive fallback — still better than visitor local time for
    // this product, but only used if Intl is broken.
    const fallback = new Date(
      date.toLocaleString("en-US", { timeZone: BERLIN_TZ }),
    );
    return {
      day: fallback.getDay(),
      hour: fallback.getHours(),
      minute: fallback.getMinutes(),
      minutes: fallback.getHours() * 60 + fallback.getMinutes(),
      date: `${fallback.getFullYear()}-${String(fallback.getMonth() + 1).padStart(2, "0")}-${String(fallback.getDate()).padStart(2, "0")}`,
    };
  }

  return {
    day,
    hour,
    minute,
    minutes: hour * 60 + minute,
    date: isoDate,
  };
}

export function timeToMinutes(time) {
  const parts = String(time || "").split(":");
  const hour = Number(parts[0]);
  const minute = Number(parts[1] || 0);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return 0;
  }

  return hour * 60 + minute;
}

export function isEnglishLocale(lang) {
  const value = String(
    lang ||
      (typeof document !== "undefined" ? document.documentElement.lang : "") ||
      "",
  ).toLowerCase();

  return value.startsWith("en");
}

export function isCzechLocale(lang) {
  const value = String(
    lang ||
      (typeof document !== "undefined" ? document.documentElement.lang : "") ||
      "",
  ).toLowerCase();

  return value.startsWith("cs");
}

// `english` wins; otherwise Czech on Czech pages, German everywhere else.
// Mirrors weissenstadt_opening_hours_text() in PHP.
function getCopy(english, compact, czech = false) {
  if (english) {
    return {
      open: "Open",
      closed: "Closed",
      unknown: "Check opening hours",
      today: "today",
      tomorrow: "tomorrow",
      days: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      daysFull: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      byAppointment: "By appointment",
      closesAt: (time) => `closes at ${time}`,
      opensAt: (day, time) => `opens ${day} at ${time}`,
      onDay: (day) =>
        compact
          ? ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][day]
          : ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][day],
      nextDay(day) {
        return `next ${this.onDay(day)}`;
      },
      reopens: (day, time) => `Reopens ${day} at ${time}`,
      nextUnknown: "Next opening time unknown",
      formatTime: formatEnglishTime,
      formatDate: (isoDate) => {
        const [year, month, day] = isoDate.split("-").map(Number);
        const date = new Date(Date.UTC(year, month - 1, day));
        const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getUTCDay()];
        const monthName = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][month - 1];
        return `${weekday}, ${day} ${monthName} ${year}`;
      },
      specialUntil: (label, date) =>
        label ? `${label} until incl. ${date}` : `until incl. ${date}`,
      specialToday: (label) => (label ? `${label} today` : "today"),
      specialAhead: (label, from, until) =>
        `${label || "closed"} from ${from} until incl. ${until}`,
    };
  }

  if (czech) {
    // Tschechisch: Wochentag als Zeitangabe im Akkusativ („ve středu“,
    // „příští středu“), Uhrzeit 24 h ohne „Uhr“, Datum „st 9. 9. 2026“.
    return {
      open: "Otevřeno",
      closed: "Zavřeno",
      unknown: "Ověřte otevírací dobu",
      today: "dnes",
      tomorrow: "zítra",
      days: ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"],
      daysFull: [
        "neděle",
        "pondělí",
        "úterý",
        "středa",
        "čtvrtek",
        "pátek",
        "sobota",
      ],
      byAppointment: "Po domluvě",
      closesAt: (time) => (compact ? `Zavírá v ${time}` : `zavírá v ${time}`),
      opensAt: (day, time) =>
        compact ? `Otevírá ${day} v ${time}` : `otevírá ${day} v ${time}`,
      onDay: (day) =>
        ["v neděli", "v pondělí", "v úterý", "ve středu", "ve čtvrtek", "v pátek", "v sobotu"][day],
      nextDay: (day) =>
        ["příští neděli", "příští pondělí", "příští úterý", "příští středu", "příští čtvrtek", "příští pátek", "příští sobotu"][day],
      reopens: (day, time) => `Znovu otevírá ${day} v ${time}`,
      nextUnknown: "Další otevírací doba zatím není známa",
      formatDate: (isoDate) => {
        const [year, month, day] = isoDate.split("-").map(Number);
        const weekday = ["ne", "po", "út", "st", "čt", "pá", "so"][
          new Date(Date.UTC(year, month - 1, day)).getUTCDay()
        ];
        return `${weekday} ${day}. ${month}. ${year}`;
      },
      specialUntil: (label, date) =>
        label ? `${label} do ${date} včetně` : `do ${date} včetně`,
      specialToday: (label) => (label ? `${label} dnes` : "dnes"),
      specialAhead: (label, from, until) =>
        `${label || "zavřeno"} od ${from} do ${until} včetně`,
      formatTime: (time) => {
        const minutes = timeToMinutes(time);
        return `${Math.floor(minutes / 60) % 24}:${String(minutes % 60).padStart(2, "0")}`;
      },
    };
  }

  return {
    open: "Geöffnet",
    closed: "Geschlossen",
    unknown: "Öffnungszeiten prüfen",
    today: "heute",
    tomorrow: "morgen",
    days: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
    daysFull: [
      "Sonntag",
      "Montag",
      "Dienstag",
      "Mittwoch",
      "Donnerstag",
      "Freitag",
      "Samstag",
    ],
    byAppointment: "Nach Vereinbarung",
    closesAt: (time) =>
      compact ? `Schließt um ${time}` : `schließt um ${time} Uhr`,
    opensAt: (day, time) =>
      compact ? `Öffnet ${day} um ${time}` : `öffnet ${day} um ${time} Uhr`,
    onDay: (day) =>
      compact
        ? ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][day]
        : ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"][day],
    nextDay(day) {
      return `nächsten ${this.onDay(day)}`;
    },
    reopens: (day, time) => `Öffnet wieder ${day} um ${time} Uhr`,
    nextUnknown: "Nächster Öffnungstermin noch nicht bekannt",
    formatDate: (isoDate) => {
      const [year, month, day] = isoDate.split("-");
      const weekday = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][
        new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).getUTCDay()
      ];
      return `${weekday}, ${day}.${month}.${year}`;
    },
    specialUntil: (label, date) =>
      label ? `${label} bis inkl. ${date}` : `bis inkl. ${date}`,
    specialToday: (label) => (label ? `${label} heute` : "heute"),
    specialAhead: (label, from, until) =>
      `${label || "geschlossen"} ab ${from} bis inkl. ${until}`,
    formatTime: (time) => {
      const minutes = timeToMinutes(time);
      const hour = Math.floor(minutes / 60) % 24;
      const minute = minutes % 60;
      return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    },
  };
}

function formatEnglishTime(time) {
  const minutes = timeToMinutes(time);
  let hour = Math.floor(minutes / 60) % 24;
  const minute = minutes % 60;
  const suffix = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  if (hour === 0) {
    hour = 12;
  }

  return minute === 0
    ? `${hour}:00 ${suffix}`
    : `${hour}:${String(minute).padStart(2, "0")} ${suffix}`;
}

/**
 * Normalize periods from data-periods JSON ([day, open, close] or objects).
 * @returns {Array<[number, string, string]>}
 */
export function normalizePeriods(periods) {
  if (!Array.isArray(periods)) {
    return [];
  }

  const normalized = [];

  for (const period of periods) {
    if (!period) {
      continue;
    }

    let day;
    let open;
    let close;

    if (Array.isArray(period)) {
      day = Number(period[0]);
      open = String(period[1] || "");
      close = String(period[2] || "");
    } else {
      day = Number(period.day);
      open = String(period.open || "");
      close = String(period.close || "");
    }

    if (
      !Number.isInteger(day) ||
      day < 0 ||
      day > 6 ||
      !open ||
      !close
    ) {
      continue;
    }

    normalized.push([day, open, close]);
  }

  return normalized;
}

const SPECIAL_STATUSES = ["open", "closed", "exceptional_unknown", "by_appointment"];

/**
 * Normalize special rows from data-special JSON: [from, to, status, label, [[open, close], …]].
 * Mirrors weissenstadt_opening_hours_normalize_special_rows() in PHP.
 * @returns {Array<{from:string,to:string,status:string,label:string,slots:Array<[string,string]>}>}
 */
export function normalizeSpecial(rows) {
  if (!Array.isArray(rows)) {
    return [];
  }

  const normalized = [];

  for (const row of rows) {
    if (!row || typeof row !== "object") {
      continue;
    }

    // Compact array row from data-special, or an already-normalized object
    // (idempotent, so callers may pass either).
    const isArray = Array.isArray(row);
    const from = String((isArray ? row[0] : row.from) || "");
    let to = String((isArray ? row[1] : row.to) || "");
    let status = String((isArray ? row[2] : row.status) || "");
    const label = String((isArray ? row[3] : row.label) || "").trim();
    const rawSlots = isArray ? row[4] : row.slots;

    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) {
      continue;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(to) || to < from) {
      to = from;
    }

    if (!SPECIAL_STATUSES.includes(status)) {
      status = "closed";
    }

    const slots = [];

    if (status === "open" && Array.isArray(rawSlots)) {
      for (const slot of rawSlots) {
        if (!Array.isArray(slot) || !slot[0] || !slot[1]) {
          continue;
        }
        slots.push([String(slot[0]).slice(0, 5), String(slot[1]).slice(0, 5)]);
      }
    }

    normalized.push({ from, to, status, label, slots });
  }

  return normalized;
}

export function specialForDate(rows, isoDate) {
  for (const row of rows) {
    if (row.from <= isoDate && isoDate <= row.to) {
      return row;
    }
  }

  return null;
}

/**
 * Normalize data-plans JSON: [validFrom, seasonFrom, seasonTo, periods].
 * Mirrors weissenstadt_einrichtung_plans_for_js() in PHP.
 * @returns {Array<{validFrom:string,seasonFrom:number,seasonTo:number,periods:Array<[number,string,string]>}>}
 */
export function parsePlansAttribute(value) {
  if (!value) {
    return [];
  }

  try {
    const rows = JSON.parse(value);
    return Array.isArray(rows)
      ? rows
          .filter(Array.isArray)
          .map((row) => {
            const from = Number(row[1]) || 0;
            const to = Number(row[2]) || 0;
            const seasonal = from >= 1 && from <= 12 && to >= 1 && to <= 12;
            return {
              validFrom: /^\d{4}-\d{2}-\d{2}$/.test(String(row[0])) ? String(row[0]) : "",
              seasonFrom: seasonal ? from : 0,
              seasonTo: seasonal ? to : 0,
              periods: normalizePeriods(row[3]),
            };
          })
      : [];
  } catch (error) {
    return [];
  }
}

function seasonCovers(plan, month) {
  if (!plan.seasonFrom) {
    return true;
  }

  return plan.seasonFrom <= plan.seasonTo
    ? month >= plan.seasonFrom && month <= plan.seasonTo
    : month >= plan.seasonFrom || month <= plan.seasonTo;
}

/**
 * The plan in force on an ISO date: matching season, latest reached start
 * date, a seasonal plan beats a year-round one on a tie. Mirrors
 * weissenstadt_einrichtung_plan_for_date() in PHP.
 */
export function planForDate(plans, isoDate) {
  const month = Number(String(isoDate).slice(5, 7));
  let best = null;

  for (const plan of plans) {
    if ((plan.validFrom && plan.validFrom > isoDate) || !seasonCovers(plan, month)) {
      continue;
    }

    if (
      !best ||
      plan.validFrom > best.validFrom ||
      (plan.validFrom === best.validFrom && !best.seasonFrom && plan.seasonFrom)
    ) {
      best = plan;
    }
  }

  return best;
}

export function parseSpecialAttribute(value) {
  if (!value) {
    return [];
  }

  try {
    return normalizeSpecial(JSON.parse(value));
  } catch (error) {
    return [];
  }
}

function specialNotice(row, today, copy) {
  if (row.status === "open") {
    return null;
  }

  let state = "closed";
  let word = copy.closed;

  if (row.status === "exceptional_unknown") {
    state = "unknown";
    word = copy.unknown;
  } else if (row.status === "by_appointment") {
    word = copy.byAppointment;
  }

  const detail =
    row.to > today
      ? copy.specialUntil(row.label, copy.formatDate(row.to))
      : copy.specialToday(row.label);

  return {
    open: false,
    state,
    label: word,
    detail,
    summary: `${word} · ${detail}`,
  };
}

// Match the server's dated reopening search, even across long holidays.
function reopeningDetail(periods, special, today, copy) {
  const last = special.reduce((date, row) => row.to > date ? row.to : date, today);
  const end = shiftIsoDate(last, 7);
  for (let date = shiftIsoDate(today, 1); date && date <= end; date = shiftIsoDate(date, 1)) {
    const row = specialForDate(special, date);
    if (row && row.status !== "open") continue;
    const day = new Date(`${date}T00:00:00Z`).getUTCDay();
    const slots = (row ? row.slots : periods.filter(([weekday]) => weekday === day).map(([, open, close]) => [open, close]))
      .filter(([open, close]) => open !== close)
      .sort((a, b) => a[0].localeCompare(b[0]));
    if (!slots.length) continue;
    let label = copy.formatDate(date);
    if (date === shiftIsoDate(today, 1)) label = `${copy.tomorrow} (${label})`;
    return copy.reopens(label, copy.formatTime(slots[0][0]));
  }
  return copy.nextUnknown;
}

/**
 * @param {Array|[number,string,string]|{day:number,open:string,close:string}>} periods
 * @param {object} [options] `english` / `czech` override the page language
 *   (documentElement.lang); `english` wins.
 * @returns {{ open: boolean, state: string, label: string, detail: string, summary: string }}
 */
export function computeOpeningStatus(periods, options = {}) {
  let list = normalizePeriods(periods);
  const special = normalizeSpecial(options.special);
  const english = options.english ?? isEnglishLocale(options.lang);
  const czech = !english && (options.czech ?? isCzechLocale(options.lang));
  const compact = Boolean(options.compact);
  const copy = getCopy(english, compact, czech);
  const clock = options.clock || getBerlinClock(options.now || new Date());
  const today = clock.date || "";

  // A special row for today outranks the weekly schedule (Betriebsurlaub,
  // Feiertag). An "open" row swaps in its own slots for today.
  const todayRow = today ? specialForDate(special, today) : null;

  if (todayRow) {
    const notice = specialNotice(todayRow, today, copy);
    if (notice) {
      notice.detail += ` · ${reopeningDetail(list, special, today, copy)}`;
      notice.summary = `${notice.label} · ${notice.detail}`;
      return notice;
    }

    if (todayRow.slots.length) {
      list = list.filter(([periodDay]) => periodDay !== clock.day);
      for (const [open, close] of todayRow.slots) {
        list.push([clock.day, open, close]);
      }
    }
  }

  if (!list.length) {
    return {
      open: false,
      state: "unknown",
      label: copy.unknown,
      detail: "",
      summary: copy.unknown,
    };
  }

  const { day, minutes } = clock;
  const previousDay = (day + 6) % 7;

  for (const [periodDay, open, close] of list) {
    const openMinutes = timeToMinutes(open);
    const closeMinutes = timeToMinutes(close);
    const isOvernight = closeMinutes < openMinutes;
    const displayClose = copy.formatTime(close);

    if (periodDay === day) {
      if (
        (!isOvernight && minutes >= openMinutes && minutes < closeMinutes) ||
        (isOvernight && minutes >= openMinutes)
      ) {
        const detail = copy.closesAt(displayClose);
        return {
          open: true,
          state: "open",
          label: copy.open,
          detail,
          summary: `${copy.open} · ${detail}`,
        };
      }
    }

    if (isOvernight && periodDay === previousDay && minutes < closeMinutes) {
      const detail = copy.closesAt(displayClose);
      return {
        open: true,
        state: "open",
        label: copy.open,
        detail,
        summary: `${copy.open} · ${detail}`,
      };
    }
  }

  let blockingRow = null;

  for (let offset = 0; offset <= 7; offset += 1) {
    const targetDay = (day + offset) % 7;
    const targetRow =
      offset > 0 && today
        ? specialForDate(special, shiftIsoDate(today, offset))
        : null;

    // "Öffnet morgen um 16:30" must not point into a Betriebsurlaub.
    if (targetRow && targetRow.status !== "open") {
      blockingRow = blockingRow || targetRow;
      continue;
    }

    const candidates = (
      targetRow && targetRow.slots.length
        ? targetRow.slots.map(([open, close]) => [targetDay, open, close])
        : list.filter(([periodDay]) => periodDay === targetDay)
    ).sort((a, b) => timeToMinutes(a[1]) - timeToMinutes(b[1]));

    for (const [, open] of candidates) {
      if (offset === 0 && timeToMinutes(open) <= minutes) {
        continue;
      }

      let dayLabel;
      if (offset === 0) {
        dayLabel = copy.today;
      } else if (offset === 1) {
        dayLabel = copy.tomorrow;
      } else if (targetDay === day) {
        // Full week wrap (e.g. only open Thursdays, checked Thursday evening).
        dayLabel = copy.nextDay(targetDay);
      } else {
        dayLabel = copy.onDay(targetDay);
      }

      const detail = copy.opensAt(dayLabel, copy.formatTime(open));
      return {
        open: false,
        state: "closed",
        label: copy.closed,
        detail,
        summary: `${copy.closed} · ${detail}`,
      };
    }
  }

  if (blockingRow) {
    const detail = copy.specialAhead(
      blockingRow.label,
      copy.formatDate(blockingRow.from),
      copy.formatDate(blockingRow.to),
    );
    return {
      open: false,
      state: "closed",
      label: copy.closed,
      detail,
      summary: `${copy.closed} · ${detail}`,
    };
  }

  return {
    open: false,
    state: "closed",
    label: copy.closed,
    detail: "",
    summary: copy.closed,
  };
}

export function parsePeriodsAttribute(value) {
  if (!value) {
    return [];
  }

  try {
    return normalizePeriods(JSON.parse(value));
  } catch (error) {
    return [];
  }
}

/**
 * Update a card / single badge element (.restaurants-card__opening).
 */
export function updateOpeningBadge(badge, status) {
  if (!badge || !status || status.state === "unknown") {
    return;
  }

  badge.hidden = false;
  badge.classList.toggle("restaurants-card__opening--open", status.open);
  badge.classList.toggle("restaurants-card__opening--closed", !status.open);
  badge.classList.remove("restaurants-card__opening--unknown");

  const label = badge.querySelector("strong");
  if (label) {
    label.textContent = status.label;
  }

  let detail = badge.querySelector(".restaurants-card__opening-detail");

  if (status.detail) {
    if (!detail) {
      detail = document.createElement("span");
      detail.className = "restaurants-card__opening-detail";
      badge.append(detail);
    }
    detail.textContent = status.detail;
  } else if (detail) {
    detail.remove();
  }
}

/**
 * Update a footer status pill label span.
 */
export function updateFooterStatusLabel(target, status) {
  if (!target || !status) {
    return;
  }

  // Prefer a nested status span for styling the open/closed word.
  let statusWord = target.querySelector(".footer-status-pill__status");
  const detailText = status.detail ? ` · ${status.detail}` : "";

  if (!statusWord) {
    target.textContent = "";
    statusWord = document.createElement("span");
    statusWord.className = "footer-status-pill__status";
    target.append(statusWord);
    target.append(document.createTextNode(detailText));
  } else {
    // Replace everything after the status word.
    while (statusWord.nextSibling) {
      statusWord.nextSibling.remove();
    }
    target.append(document.createTextNode(detailText));
  }

  statusWord.textContent = status.label;

  const pill = target.closest(".footer-status-pill");
  if (pill) {
    pill.classList.toggle("is-currently-open", status.open);
    pill.classList.toggle("is-currently-closed", !status.open && status.state !== "unknown");
    pill.dataset.open = status.open ? "1" : "0";
  }
}

/**
 * Refresh every [data-opening-status] node that carries data-periods.
 * Works for restaurant cards, gastgeber singles, footer pills.
 *
 * @returns {boolean} Whether any open/closed flag changed.
 */
export function refreshAllOpeningStatuses(options = {}) {
  const roots = options.root
    ? options.root.querySelectorAll("[data-opening-status]")
    : document.querySelectorAll("[data-opening-status]");
  const english = options.english ?? isEnglishLocale();
  const czech = options.czech ?? isCzechLocale();
  const clock = options.clock || getBerlinClock(options.now || new Date());
  let changed = false;

  roots.forEach((node) => {
    const plans = parsePlansAttribute(node.getAttribute("data-plans"));
    const special = parseSpecialAttribute(node.getAttribute("data-special"));
    // Dated plans (Einrichtungen) let a cached page switch to new hours or
    // the next season on its own; data-periods is only the render-day plan.
    const periods = plans.length
      ? planForDate(plans, clock.date)?.periods || []
      : parsePeriodsAttribute(node.getAttribute("data-periods"));
    if (!periods.length && !special.length && !plans.length) {
      return;
    }

    const compact = node.getAttribute("data-opening-compact") === "1";
    const status = computeOpeningStatus(periods, {
      english,
      czech,
      compact,
      clock,
      special,
    });
    const openValue = status.open ? "1" : "0";
    const prevOpen = node.getAttribute("data-open");

    if (prevOpen !== openValue) {
      changed = true;
    }

    node.setAttribute("data-open", openValue);
    node.setAttribute("data-opening-state", status.state);

    const badge = node.matches(".restaurants-card__opening, .gastgeber-single__opening")
      ? node
      : node.querySelector(".restaurants-card__opening, .gastgeber-single__opening");

    if (badge) {
      updateOpeningBadge(badge, status);
    }

    const footerLabel = node.querySelector("[data-opening-label]");
    if (footerLabel) {
      updateFooterStatusLabel(footerLabel, status);
    } else if (node.hasAttribute("data-opening-label")) {
      updateFooterStatusLabel(node, status);
    }
  });

  return changed;
}

/**
 * Auto-bind: recompute on load, every minute, and on bfcache restore.
 */
export function bindOpeningStatusAutoRefresh(options = {}) {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }

  const intervalMs = options.intervalMs || 60000;

  const run = () => refreshAllOpeningStatuses(options);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  } else {
    run();
  }

  window.setInterval(run, intervalMs);

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      run();
    }
  });

  // Visibility change: correct immediately after a long background tab.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      run();
    }
  });
}

// Kein Seiteneffekt hier: bundle.js ruft bindOpeningStatusAutoRefresh() auf.
// So kann die Merkliste (merkliste-bundle.js) die reinen Funktionen nutzen,
// ohne einen zweiten Minutentakt zu starten.
